/** @graphql agg **/
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { graphql } from 'react-relay';

import { useSmartLinkContext } from '@atlaskit/link-provider/use-smart-link-context';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { buildContentAri, type BylineContentType } from '../utils/contentAri.ts';
import { buildIncomingLinksQueryVariables } from '../utils/buildIncomingLinksQueryVariables.ts';
import { buildAggQueryContextHeaders } from '../utils/aggQueryContext.ts';
import { appendNewLinks } from '../utils/appendNewLinks.ts';
import { hydrateIncomingLinks } from '../utils/hydrateIncomingLinks.ts';
import type { EnhancedSmartLink } from '../utils/smartLinkTypes.ts';

import type {
	useIncomingLinksQuery$data,
	useIncomingLinksQuery$variables,
} from './__generated__/useIncomingLinksQuery.graphql.ts';
import { getIncomingLinksRemoteLinksSucceededEvent } from './incomingLinksAnalytics.ts';
import { useIncomingLinksGraphPages } from './useIncomingLinksGraphPages.ts';
import { useIncomingLinksRemoteLinks } from './useIncomingLinksRemoteLinks.ts';
import { useIncomingLinksQueryAnalytics } from './useIncomingLinksQueryAnalytics.ts';
import { useDependencyHealthAnalytics } from './useDependencyHealthAnalytics.ts';

// A full batch up front: many incoming rows are dropped before they can be
// rendered (an entity type the catalog does not cover, a null url, or a url ORS
// has no provider for), so a small page can resolve to an empty section. 50 also
// matches link-provider's per-hostname ORS batch size, so a page's urls are
// resolved in as few requests as the client allows.
export const DEFAULT_INITIAL_PAGE_SIZE = 50;
export const DEFAULT_LOAD_MORE_PAGE_SIZE = 50;

// The preload uses this query too, so the browser can reuse the complete first page.
const IncomingLinksQuery = graphql`
	query useIncomingLinksQuery($cypherQuery: String!, $first: Int, $after: String) {
		graphStore_cypherQueryV2(query: $cypherQuery, first: $first, after: $after)
			@optIn(to: "GraphStoreCypherQueryV2") {
			pageInfo {
				hasNextPage
				endCursor
			}
			edges {
				node {
					columns {
						key
						value {
							__typename
							... on GraphStoreCypherQueryV2AriNode {
								id
								data {
									# The per-type URL selection, shared with useRelatedContentQuery; defined in incomingLinkTypes.ts.
									...incomingLinkTypes_node @relay(mask: false)
								}
							}
						}
					}
				}
			}
		}
	}
`;

export type UseIncomingLinksOptions = {
	initialPageSize?: number;
	loadMorePageSize?: number;
	/**
	 * Set to `false` to hold the fetch. Lets a caller that is mounted before its UI is
	 * reachable — the Teamwork Graph modal stays mounted while closed — keep the query
	 * off the view page until it is opened. Defaults to `true`.
	 */
	enabled?: boolean;
	/** Selects the graph node type and ARI the content is stored under. Undefined means page. */
	contentType?: BylineContentType;
};

export type UseIncomingLinksResult = {
	/** Every incoming link to list: graph rows followed by remote links. */
	links: EnhancedSmartLink[];
	/**
	 * Rows from the Teamwork Graph query. The byline's show/hide rule and icon stack read these
	 * alone: remote links cannot be server-side rendered and land after first paint, so letting
	 * them count would shift the page or repaint the trigger. CCINT-9346
	 */
	twgLinks: EnhancedSmartLink[];
	loading: boolean;
	/**
	 * True once nothing more can land without a `loadMore`: the graph rows have hydrated and the
	 * remote-link fallback has resolved, failed or timed out. Unlike `loading`, this waits for the
	 * fallback, so a count derived from `links` (the byline menu's "Appears in (N+)") does not change
	 * under the user. CCINT-9385
	 */
	settled: boolean;
	loadingMore: boolean;
	hasNextPage: boolean;
	endCursor: string | null;
	loadMore: () => void;
	error: ApolloErrorInterop | undefined;
};

/**
 * Fetches cross-product incoming links to a Confluence page (Jira issues,
 * Townsquare goals, other pages, etc. that reference it) from the Teamwork
 * Graph via the experimental AGG `graphStore_cypherQueryV2` field, then hydrates
 * each entity URL into an `EnhancedSmartLink` via ORS — the same resolution path
 * the outgoing links use. Returns a flat list.
 *
 * Must be rendered under a SmartCard provider (`useSmartLinkContext`).
 */
export const useIncomingLinks = (
	contentId: string | undefined,
	options?: UseIncomingLinksOptions,
): UseIncomingLinksResult => {
	const initialPageSize = options?.initialPageSize ?? DEFAULT_INITIAL_PAGE_SIZE;
	const loadMorePageSize = options?.loadMorePageSize ?? DEFAULT_LOAD_MORE_PAGE_SIZE;
	const enabled = options?.enabled ?? true;
	const contentType = options?.contentType;

	const { cloudId } = useSessionData();
	const { connections, store } = useSmartLinkContext();
	const reportHealth = useDependencyHealthAnalytics();

	// `@confluence/analytics`, not the platform analytics-bridge helper. `fireOperationalAnalytics`
	// fires on the `analyticsBridge` channel, and `AnalyticsListener` only invokes its handler when
	// its own `channel` is `'*'` or equal to the event's; `ConfluenceAnalyticsListener` passes no
	// `channel`, so bridge events never reached it at all. See `.agents/incoming-links.md`. CCINT-9105
	const { createEvent } = useConfluenceAnalyticsEvents();

	// The SmartCard provider rebuilds its context value with `lodash.merge({}, ...)`, which mints a
	// new `store` object on every recompute even though the underlying Redux store is unchanged. We
	// read it through a ref and keep it out of the hydration callback's deps so an ancestor re-render
	// does not re-create the callback and re-fire the hydration effect.
	const storeRef = useRef(store);
	useEffect(() => {
		storeRef.current = store;
	}, [store]);

	const skip = !enabled || !contentId || !cloudId;

	// Latches the merge so a graph refetch cannot un-merge the remote links or re-fire their event.
	const remoteLinksMergedRef = useRef(false);

	const {
		data,
		loading: queryLoading,
		error,
		fetchMore,
	} = useQueryInterop<useIncomingLinksQuery$data, useIncomingLinksQuery$variables>(
		IncomingLinksQuery,
		{
			variables: skip
				? { cypherQuery: '', first: initialPageSize, after: null }
				: buildIncomingLinksQueryVariables(
						buildContentAri(cloudId, contentId, contentType),
						contentType,
						initialPageSize,
					),
			skip,
			interopApolloAggClient: true,
			// Keep partial data on GraphQL errors. Hydrating cross-activation 3P
			// entities can raise a per-node error ("Cross activation id requests are
			// not allowed") while still returning the rest of the rows; with the
			// default 'none' policy that partial data is discarded before it reaches
			// us. 'all' surfaces `data` alongside `error` so we can render what came
			// back. Report from the returned error; not every route calls onError for partial data.
			errorPolicy: 'all',
			context: skip ? undefined : { headers: buildAggQueryContextHeaders(cloudId) },
		},
	);
	const readInitialQueryDuration = useIncomingLinksQueryAnalytics({
		enabled: !skip,
		identity: JSON.stringify([cloudId, contentId, contentType, initialPageSize]),
		loading: queryLoading,
		data,
		error,
	});

	const hydrateLinks = useCallback(
		(urls: string[]) =>
			hydrateIncomingLinks(urls, storeRef.current, connections.client, reportHealth),
		[connections.client, reportHealth],
	);

	const graph = useIncomingLinksGraphPages({
		data,
		queryLoading,
		fetchMore,
		skip,
		loadMorePageSize,
		hydrateLinks,
		readInitialQueryDuration,
	});
	const graphLinks = graph.links;

	const remoteLinksResult = useIncomingLinksRemoteLinks(contentId, { skip, hydrateLinks });

	// Merge only once the graph has settled (initial hydration committed, or the query returned no
	// data) so its recency-ordered rows stay ahead of the appended remote links.
	const remoteLinksMerged =
		remoteLinksResult != null && (graph.settled || remoteLinksMergedRef.current);

	// `graphLinks` state stays graph-only; the remote links are appended as a derived view so a later
	// graph page that ingests the same work item keeps the graph row and drops the remote copy.
	const mergedLinks = useMemo(
		() =>
			remoteLinksMerged && remoteLinksResult?.status === 'resolved'
				? appendNewLinks(graphLinks, remoteLinksResult.links)
				: graphLinks,
		[graphLinks, remoteLinksMerged, remoteLinksResult],
	);

	useEffect(() => {
		if (!remoteLinksMerged || remoteLinksMergedRef.current) {
			return;
		}
		remoteLinksMergedRef.current = true;

		if (remoteLinksResult?.status === 'resolved') {
			createEvent(
				getIncomingLinksRemoteLinksSucceededEvent({
					fetched: remoteLinksResult.fetched,
					resolved: remoteLinksResult.links.length,
					added: mergedLinks.length - graphLinks.length,
					...remoteLinksResult.timings,
				}),
			).fire();
		}
	}, [remoteLinksMerged, remoteLinksResult, graphLinks, mergedLinks, createEvent]);

	// `loading` deliberately ignores the remote-link fallback: nothing painted depends on it.
	// `settled` is the flag for the one reader that does (the menu's section count).
	return {
		links: mergedLinks,
		twgLinks: graphLinks,
		loading: graph.loading,
		settled: skip || (!graph.loading && remoteLinksMerged),
		loadingMore: graph.loadingMore,
		hasNextPage: graph.hasNextPage,
		endCursor: graph.endCursor,
		loadMore: graph.loadMore,
		error,
	};
};
